import { Component, OnInit, ViewChild, ElementRef, OnDestroy } from '@angular/core';
import {FormsModule} from '@angular/forms';
import {CommonModule, DatePipe} from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BackButton } from '@components/ui/back-button/back-button';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import { UserTicket, UserTicketService, CreateMessageCommand, UserTicketMessageMessageGetQuery, UserTicketMessage, UserTicketBaseGetQuery } from '@6grain/masofa-monolith-api-client/common';
import { FileStorageItemService } from '@6grain/masofa-monolith-api-client/common/api/fileStorageItem.service';
import { of, firstValueFrom, forkJoin, Subject } from 'rxjs';
import { catchError, map, takeUntil } from 'rxjs/operators';
import {Loading} from '@components/ui/loading/loading';
import { InputComponent } from '@components/input/input.component';
import { FileSendModalComponent, FileToSend } from '@components/file-send-modal';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { ButtonComponent } from '@app/components/button/button.component';
import { TagModule } from 'primeng/tag';
import { DateFormatLangPipe } from '@app/utils/pipes/date-format-lang-pipe';
import { UserProfileService } from '@services/user-profile.service';
import { environment } from '@root/src/environments/environment';
import { ConfirmationModalComponent } from '@app/components/confirmation-modal/confirmation-modal.component';

interface ChatMessage {
  id: string;
  text: string;
  time: string;
  isOwn: boolean;
  isRead?: boolean;
  isSystem?: boolean;
  senderName?: string;
  attachments?: ChatAttachment[];
}

interface ChatItem {
  id: string;
  name: string;
  avatar: string;
  lastMessage: string;
  lastTime: string;
  lastSeen?: string;
  status: 'online' | 'away' | 'offline';
  unreadCount?: number;
}

interface UploadAttachmentResponse {
  fileId?: string;
  FileId?: string;
  id?: string;
  Id?: string;
  fileName?: string;
  FileName?: string;
  fileSize?: number;
  FileSize?: number;
  contentType?: string;
  ContentType?: string;
}

interface ChatAttachment {
  id: string;
  url: string;
  displayName: string;
  fileName?: string;
  contentType?: string;
  fileSize?: number;
}

interface AttachmentMetadata {
  id: string;
  fileName: string;
  contentType?: string;
  fileSize?: number;
}

@Component({
  selector: 'app-chat',
  templateUrl: './chat.component.html',
  imports: [
    FormsModule,
    CommonModule,
    BackButton,
    TranslatePipe,
    Loading,
    InputComponent,
    FileSendModalComponent,
    DialogModule,
    ButtonModule,
    ButtonComponent,
    TagModule,
    DateFormatLangPipe,
    ConfirmationModalComponent
  ],
  providers: [DatePipe],
  styleUrls: ['./chat.component.scss'],
  standalone: true,
})
export class ChatComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();

  @ViewChild('messagesContainer') messagesContainer!: ElementRef;
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

  sidebarOpen = false;
  searchQuery = '';
  messageSearchQuery = '';
  messageText = '';
  activeChat: ChatItem | null = null;
  showScrollDown = false;
  private scrollThreshold = 64;
  currentTicketId: string | null = null;
  currentUserTicket: UserTicket | null = null;
  loading = false;
  sendingMessage = false;

  chatList: ChatItem[] = [];
  allUserTickets: UserTicket[] = [];

  messages: ChatMessage[] = [];
  allMessages: ChatMessage[] = []; // Store all messages for filtering
  filteredMessages: ChatMessage[] = []; // Store filtered messages
  loadingMessages = false;

  selectedFiles: File[] = [];
  uploadingFiles = false;
  showFileSendModal = false;
  isRecordingVoice = false;
  showEmojiPicker = false;
  currentUserName = '';
  currentAppealInfo: { moduleName?: string; exceptionType?: string; fullTicket?: UserTicket } = {};
  showAppealDetailsModal = false;
  showExceptionJson = false;
  showJsonDetailsModal = false;
  currentJsonData = '';
  currentUserId: string | null = null;
  
  // Confirmation modals
  showCloseTicketModal = false;
  showRejectTicketModal = false;
  pendingStatusAction: number | null = null;

  private attachmentMetadataCache = new Map<string, AttachmentMetadata>();
  private pendingAttachmentRequests = new Map<string, Promise<AttachmentMetadata | null>>();

  public translate: TranslateService;

  constructor(
    private route: ActivatedRoute,
    private userTicketService: UserTicketService,
    private userProfileService: UserProfileService,
    private http: HttpClient,
    private fileStorageItemService: FileStorageItemService,
    translate: TranslateService
  ) {
    this.translate = translate;
  }

  ngOnInit(): void {
    this.route.params.subscribe(params => {
      const ticketId = params['id'];
      this.currentTicketId = ticketId || null;

      if (this.currentTicketId) {
        this.loadSingleTicket(this.currentTicketId);
      } else {
        this.loadAllUserTickets();
      }
      this.loadCurrentUser();
    });
  }

  private loadSingleTicket(ticketId: string): void {
    this.loading = true;
    this.userTicketService.commonUserTicketGetByIdIdGet(ticketId)
      .pipe(
        catchError((_error) => {
          this.loading = false;
          return of(null);
        }), takeUntil(this.destroy$)
      )
      .subscribe(userTicket => {
        this.loading = false;
        if (userTicket) {
          this.allUserTickets = [userTicket];
          this.transformUserTicketToChatItem(userTicket);
        }
      });
  }

  private loadCurrentUser(): void {
    const profile = this.userProfileService.getProfile();
    this.currentUserName = profile?.userName || 'You';
    this.currentUserId = profile?.id || null;
  }

  private loadAllUserTickets(): void {
    this.loading = true;

    const query: UserTicketBaseGetQuery = {
      take: 1000,
      offset: 0,
      filters: []
    };

    this.userTicketService.commonUserTicketGetByQueryPost(query)
      .pipe(
        catchError((error) => {
          this.loading = false;
          return of([]);
        })
      )
      .subscribe((userTickets: UserTicket[]) => {
        this.loading = false;
        this.allUserTickets = userTickets || [];

        this.transformAllUserTicketsToChatList();
        this.loadLastMessagesForChatList();

        if (this.currentTicketId) {
          this.selectChatById(this.currentTicketId);
        }
        else if (this.chatList.length > 0) {
          this.selectChat(this.chatList[0]);
        }
      });
  }

  private selectChatById(ticketId: string): void {
    const matchingChat = this.chatList.find(chat => chat.id === ticketId);

    if (matchingChat) {
      const matchingUserTicket = this.allUserTickets.find(ticket => ticket.id === ticketId);
      if (matchingUserTicket) {
        this.currentUserTicket = matchingUserTicket;
      }
      this.selectChat(matchingChat);
    }
    else if (this.chatList.length > 0) {
      this.selectChat(this.chatList[0]);
    }
  }

  private filterAndPopulateChatListById(ticketId: string): void {
    const matchingTickets = this.allUserTickets.filter(ticket => ticket.id === ticketId);

    if (matchingTickets.length > 0) {
      this.chatList = matchingTickets.map(ticket => this.createChatItemFromUserTicket(ticket));
      this.loadLastMessagesForChatList();
      this.currentUserTicket = matchingTickets[0];
      this.selectChat(this.chatList[0]);
    } else {
      this.transformAllUserTicketsToChatList();
      this.loadLastMessagesForChatList();
      if (this.chatList.length > 0) {
        this.selectChat(this.chatList[0]);
      }
    }
  }

  private transformAllUserTicketsToChatList(): void {
    this.chatList = this.allUserTickets.map(userTicket =>
      this.createChatItemFromUserTicket(userTicket)
    );
  }

  private createChatItemFromUserTicket(userTicket: UserTicket): ChatItem {
    return {
      id: userTicket.id || Date.now().toString(),
      name: userTicket.createUserName || this.translate.instant('Unknown user'),
      avatar: '/assets/icons/user-default.svg',
      lastMessage: this.getNoMessageText(),
      lastTime: this.formatTime(userTicket.lastUpdateAt || userTicket.createAt),
      lastSeen: userTicket.lastUpdateAt ? `${this.translate.instant('Last activity')} ${this.formatTime(userTicket.lastUpdateAt)}` : undefined,
      status: this.getStatusFromUserTicket(userTicket),
      unreadCount: 0
    };
  }

  private loadUserTicket(ticketId: string): void {
    const existingTicket = this.allUserTickets.find(ticket => ticket.id === ticketId);
    if (existingTicket) {
      this.filterAndPopulateChatListById(ticketId);
      return;
    }

    this.loading = true;
    this.userTicketService.commonUserTicketGetByIdIdGet(ticketId)
      .pipe(
        catchError((_error) => {
          this.loading = false;
          return of(null);
        }), takeUntil(this.destroy$)
      )
      .subscribe(userTicket => {
        this.loading = false;
        if (userTicket) {
          this.transformUserTicketToChatItem(userTicket);
        }
      });
  }

  private transformUserTicketToChatItem(userTicket: UserTicket): void {
    this.currentUserTicket = userTicket;
    const chatItem = this.createChatItemFromUserTicket(userTicket);
    this.chatList = [chatItem];
    this.loadLastMessagesForChatList();
    this.selectChat(chatItem);
  }

  private formatTime(dateString?: string): string {
    if (!dateString) return '';

    try {
      const date = new Date(dateString);
      const now = new Date();

      const diffMs = now.getTime() - date.getTime();
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

      if (diffDays === 0) {
        return date.toLocaleTimeString('en-US', {
          hour: '2-digit',
          minute: '2-digit'
        });
      }

      if (diffDays === 1) {
        return `Yesterday ${date.toLocaleTimeString('en-US', {
          hour: '2-digit',
          minute: '2-digit'
        })}`;
      }

      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });

    } catch {
      return '';
    }
  }

  public getStatusFromUserTicket(userTicket: UserTicket): 'online' | 'away' | 'offline' {
    if (userTicket.status === 1) return 'online';
    if (userTicket.status === 2) return 'away';
    if (userTicket.status === 0) return 'offline';
    return 'offline';
  }

  public getLocalizedStatus(status: 'online' | 'away' | 'offline'): string {
    return this.translate.instant(status);
  }

  onMessagesScroll(): void {
    if (!this.messagesContainer) return;
    const el = this.messagesContainer.nativeElement as HTMLElement;
    const distanceFromBottom = el.scrollHeight - el.clientHeight - el.scrollTop;
    this.showScrollDown = distanceFromBottom > this.scrollThreshold;
  }

  toggleSidebar(): void {
    this.sidebarOpen = !this.sidebarOpen;
  }

  selectChat(chat: ChatItem): void {
    this.activeChat = chat;
    this.sidebarOpen = false;
    this.loadMessagesForChat(chat.id);
    this.loadAppealInfo(chat.id);
  }

  private loadAppealInfo(chatId: string): void {
    const userTicket = this.allUserTickets.find(ticket => ticket.id === chatId);
    
    if (userTicket) {
      this.currentAppealInfo = {
        moduleName: userTicket.moduleName || 'Unknown Module',
        exceptionType: userTicket.exceptionType || 'Unknown Type',
        fullTicket: userTicket
      };
    } else {
      this.currentAppealInfo = {
        moduleName: 'No Module Found',
        exceptionType: 'No Type Found',
        fullTicket: undefined
      };
    }
  }

  showAppealDetails(): void {
    this.showAppealDetailsModal = true;
  }

  hideAppealDetails(): void {
    this.showAppealDetailsModal = false;
    this.showExceptionJson = false;
  }

  toggleExceptionJson(): void {
    this.showExceptionJson = !this.showExceptionJson;
  }

  showJsonModal(jsonData: string): void {
    this.currentJsonData = jsonData;
    this.showJsonDetailsModal = true;
  }

  hideJsonModal(): void {
    this.showJsonDetailsModal = false;
    this.currentJsonData = '';
  }

  isJsonLong(jsonData: string): boolean {
    return !!(jsonData && jsonData.length > 100);
  }

  getTruncatedJson(jsonData: string): string {
    if (!jsonData) return '';
    if (jsonData.length <= 100) return jsonData;
    return jsonData.substring(0, 100) + '...';
  }

  formatJson(jsonData: string): string {
    try {
      const parsed = JSON.parse(jsonData);
      return JSON.stringify(parsed, null, 2);
    } catch (e) {
      return jsonData;
    }
  }

  private loadMessagesForChat(chatId: string): void {
    if (!chatId) return;

    this.loadingMessages = true;

    const messageQuery: UserTicketMessageMessageGetQuery = {
      take: 100,
      offset: 0,
      sortBy: 'Created',
      sort: 1,
      filters: [
        {
          filterField: 'UserTicketId',
          filterValue: chatId,
          filterOperator: 0
        }
      ]
    };

    this.userTicketService.commonUserTicketGetMessageByQueryPost(messageQuery)
      .pipe(
        catchError(error => {
          this.loadingMessages = false;
          console.error('Error loading messages:', error);
          return of([]);
        })
      )
      .subscribe((apiMessages: UserTicketMessage[]) => {
        this.loadingMessages = false;
        this.allMessages = this.convertApiMessagesToChatMessages(apiMessages);
        this.messages = [...this.allMessages];
        setTimeout(() => this.scrollToBottom(), 100);
      });
  }

  private convertApiMessagesToChatMessages(apiMessages: UserTicketMessage[]): ChatMessage[] {
    if (!apiMessages || apiMessages.length === 0) {
      return [{
        id: 'system-1',
        text: this.translate.instant('chat.conversation_start') || 'Start of conversation',
        time: '',
        isOwn: false,
        isSystem: true
      }];
    }

    const sortedMessages = apiMessages.sort((a, b) => {
      const dateA = new Date(a.created || '').getTime();
      const dateB = new Date(b.created || '').getTime();
      return dateA - dateB;
    });

    const chatMessages: ChatMessage[] = [];

    chatMessages.push({
      id: 'system-start',
      text: this.formatDate(sortedMessages[0]?.created),
      time: '',
      isOwn: false,
      isSystem: true
    });

    sortedMessages.forEach(apiMessage => {
      chatMessages.push(this.mapApiMessageToChatMessage(apiMessage));
    });

    const lastContentMessage = [...chatMessages]
      .reverse()
      .find(message => !message.isSystem);

    if (this.activeChat) {
      this.updateChatPreview(this.activeChat.id, lastContentMessage ?? null);
    }

    return chatMessages;
  }

  private loadLastMessagesForChatList(): void {
    if (!this.chatList.length) {
      return;
    }

    const queries = this.chatList
      .filter(chat => !!chat.id)
      .map(chat => {
        const messageQuery: UserTicketMessageMessageGetQuery = {
          take: 1,
          offset: 0,
          sortBy: 'Created',
          sort: 1,
          filters: [
            {
              filterField: 'UserTicketId',
              filterValue: chat.id,
              filterOperator: 0
            }
          ]
        };

        return this.userTicketService
          .commonUserTicketGetMessageByQueryPost(messageQuery)
          .pipe(
            map((messages: UserTicketMessage[]) => {
              if (!messages || messages.length === 0) {
                this.updateChatPreview(chat.id, null);
                return;
              }

              const latestMessage = this.getLatestMessage(messages);
              const chatMessage = this.mapApiMessageToChatMessage(latestMessage, { senderName: chat.name });
              this.updateChatPreview(chat.id, chatMessage);
            }),
            catchError(() => {
              this.updateChatPreview(chat.id, null);
              return of(null);
            })
          );
      });

    if (!queries.length) {
      return;
    }

    forkJoin(queries).subscribe({
      error: (error) => console.error('Error loading last messages for chats:', error)
    });
  }

  private getLatestMessage(messages: UserTicketMessage[]): UserTicketMessage {
    return messages.reduce((latest, current) => {
      const latestTime = new Date(latest?.created || '').getTime();
      const currentTime = new Date(current?.created || '').getTime();
      return currentTime > latestTime ? current : latest;
    }, messages[0]);
  }

  private getNoMessageText(): string {
    const translated = this.translate.instant('chat.no_message');
    if (translated && translated !== 'chat.no_message') {
      return translated;
    }

    const fallback = this.translate.instant('No message');
    return fallback || 'No message';
  }

  private extractAttachmentIds(apiMessage: UserTicketMessage): string[] {
    const directIds = (apiMessage as any)?.attachmentIds;
    if (Array.isArray(directIds) && directIds.length > 0) {
      return directIds
        .map(id => this.normalizeAttachmentId(id))
        .filter((id): id is string => !!id);
    }

    const attachmentIdsJson = (apiMessage as any)?.attachmentIdsJson;
    const parsed = this.parseAttachmentIdsJson(attachmentIdsJson);
    return parsed
      .map(id => this.normalizeAttachmentId(id))
      .filter((id): id is string => !!id);
  }

  private parseAttachmentIdsJson(jsonValue: any): any[] {
    if (!jsonValue) {
      return [];
    }

    try {
      const parsed = typeof jsonValue === 'string' ? JSON.parse(jsonValue) : jsonValue;
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  private normalizeAttachmentId(value: any): string | null {
    if (!value) {
      return null;
    }

    if (typeof value === 'string') {
      return value;
    }

    if (typeof value === 'object' && typeof value.toString === 'function') {
      const converted = value.toString();
      return converted && converted !== '[object Object]' ? converted : null;
    }

    return null;
  }

  private createAttachmentsFromIds(ids: string[], sourceFiles?: Array<{ name?: string }>): ChatAttachment[] {
    if (!ids || !ids.length) {
      return [];
    }

    const attachments = ids.map((id, index) => {
      const normalizedId = id.toString();
      return {
        id: normalizedId,
        url: this.buildAttachmentUrl(normalizedId),
        displayName: sourceFiles?.[index]?.name || `Attachment ${index + 1}`
      };
    });

    this.populateAttachmentMetadata(attachments);

    return attachments;
  }

  private buildAttachmentUrl(attachmentId: string): string {
    return `${environment.api}/common/FileStorageItem/GetFile/${attachmentId}`;
  }

  private populateAttachmentMetadata(attachments: ChatAttachment[]): void {
    attachments.forEach(attachment => {
      this.ensureAttachmentMetadata(attachment).catch(error => {
        console.error('Failed to populate attachment metadata', attachment.id, error);
      });
    });
  }

  private async ensureAttachmentMetadata(attachment: ChatAttachment): Promise<void> {
    if (!attachment || !attachment.id) {
      return;
    }

    if (this.attachmentMetadataCache.has(attachment.id)) {
      const metadata = this.attachmentMetadataCache.get(attachment.id);
      if (metadata) {
        this.applyAttachmentMetadata(attachment, metadata);
      }
      return;
    }

    if (!this.pendingAttachmentRequests.has(attachment.id)) {
      const request = firstValueFrom(this.fileStorageItemService.commonFileStorageItemGetByIdIdGet(attachment.id))
        .then(fileInfo => {
          if (!fileInfo) {
            return null;
          }

          const metadata = this.mapFileInfoToMetadata(attachment.id, fileInfo);
          this.attachmentMetadataCache.set(attachment.id, metadata);
          return metadata;
        })
        .catch(error => {
          console.error('Error fetching attachment metadata:', attachment.id, error);
          return null;
        })
        .finally(() => {
          this.pendingAttachmentRequests.delete(attachment.id);
        });

      this.pendingAttachmentRequests.set(attachment.id, request);
    }

    const metadata = await this.pendingAttachmentRequests.get(attachment.id);
    if (metadata) {
      this.applyAttachmentMetadata(attachment, metadata);
    }
  }

  private mapFileInfoToMetadata(id: string, fileInfo: any): AttachmentMetadata {
    const fileName =
      fileInfo?.fileName ||
      fileInfo?.FileName ||
      this.extractFileNameFromPath(fileInfo?.fileStoragePath || fileInfo?.FileStoragePath);

    const contentType = fileInfo?.fileContentTypeName || fileInfo?.FileContentTypeName || fileInfo?.fileContentType;
    const fileSize = fileInfo?.fileLength ?? fileInfo?.FileLength;

    return {
      id,
      fileName: fileName || `Attachment ${id}`,
      contentType: contentType || undefined,
      fileSize: typeof fileSize === 'number' ? fileSize : undefined
    };
  }

  private extractFileNameFromPath(path?: string): string {
    if (!path) {
      return '';
    }

    const segments = path.split(/[/\\]/).filter(Boolean);
    return segments.length ? segments[segments.length - 1] : path;
  }

  private applyAttachmentMetadata(attachment: ChatAttachment, metadata: AttachmentMetadata): void {
    if (!metadata) {
      return;
    }

    attachment.fileName = metadata.fileName || attachment.fileName;
    attachment.displayName = metadata.fileName || attachment.displayName;
    attachment.contentType = metadata.contentType || attachment.contentType;
    attachment.fileSize = metadata.fileSize ?? attachment.fileSize;
  }

  async downloadAttachment(attachment: ChatAttachment, event: Event): Promise<void> {
    event.preventDefault();

    if (!attachment?.id) {
      return;
    }

    await this.ensureAttachmentMetadata(attachment);

    const fileName = attachment.fileName || attachment.displayName || `attachment-${attachment.id}`;

    try {
      const blob = await firstValueFrom(
        this.http.get(attachment.url, {
          responseType: 'blob'
        })
      );

      const downloadUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Error downloading attachment:', error);
      window.open(attachment.url, '_blank');
    }
  }

  private mapApiMessageToChatMessage(apiMessage: UserTicketMessage, options?: { senderName?: string }): ChatMessage {
    const isOwn = this.isOwnMessage(apiMessage);
    const chatMessage: ChatMessage = {
      id: apiMessage.id || Date.now().toString(),
      text: apiMessage.message || '',
      time: this.formatTime(apiMessage.created),
      isOwn,
      isRead: true,
      senderName: isOwn
        ? this.currentUserName
        : options?.senderName || this.activeChat?.name || this.getDefaultSenderName()
    };

    const attachments = this.createAttachmentsFromIds(this.extractAttachmentIds(apiMessage));
    if (attachments.length) {
      chatMessage.attachments = attachments;
    }

    return chatMessage;
  }

  private updateChatPreview(chatId: string | null | undefined, message: ChatMessage | null): void {
    if (!chatId) {
      return;
    }

    const chatIndex = this.chatList.findIndex(chat => chat.id === chatId);
    if (chatIndex === -1) {
      return;
    }

    const previewText = this.buildPreviewText(message);
    const previewTime = message?.time || '';

    this.chatList[chatIndex].lastMessage = previewText;
    this.chatList[chatIndex].lastTime = previewTime;

    if (this.activeChat?.id === chatId) {
      this.activeChat.lastMessage = previewText;
      this.activeChat.lastTime = previewTime;
    }
  }

  private buildPreviewText(message: ChatMessage | null): string {
    if (!message) {
      return this.getNoMessageText();
    }

    const attachmentsCount = message.attachments?.length ?? 0;
    const trimmedText = message.text?.trim() ?? '';

    let content = trimmedText;
    if (!content && attachmentsCount > 0) {
      content = this.getAttachmentLabel(attachmentsCount);
    }

    if (!content) {
      content = this.getNoMessageText();
    }

    if (message.isOwn) {
      const youLabel = this.translate.instant('chat.you');
      const prefix = youLabel && youLabel !== 'chat.you' ? youLabel : 'You';
      return `${prefix}: ${content}`;
    }

    if (message.senderName) {
      return `${message.senderName}: ${content}`;
    }

    return content;
  }

  private getAttachmentLabel(count: number): string {
    if (count <= 0) {
      return '';
    }

    if (count === 1) {
      const singular = this.translate.instant('chat.attachment');
      return singular && singular !== 'chat.attachment' ? singular : 'Attachment';
    }

    const plural = this.translate.instant('chat.attachments');
    const base = plural && plural !== 'chat.attachments' ? plural : 'Attachments';
    return `${base} (${count})`;
  }

  private getDefaultSenderName(): string {
    const translated = this.translate.instant('chat.support_agent');
    if (translated && translated !== 'chat.support_agent') {
      return translated;
    }

    const fallback = this.translate.instant('Support');
    return fallback && fallback !== 'Support' ? fallback : 'Support';
  }

  private isOwnMessage(apiMessage: UserTicketMessage): boolean {
    if (!apiMessage || !apiMessage.emailId) {
      return false;
    }

    if (this.currentUserId) {
      return apiMessage.emailId === this.currentUserId;
    }

    const ticketCreatorId = this.currentUserTicket?.createUser;
    return ticketCreatorId ? apiMessage.emailId !== ticketCreatorId : false;
  }

  private formatDate(dateString?: string): string {
    if (!dateString) return this.translate.instant('chat.today') || 'Today';

    try {
      const date = new Date(dateString);
      const today = new Date();

      if (date.toDateString() === today.toDateString()) {
        return this.translate.instant('chat.today') || 'Today';
      }

      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);

      if (date.toDateString() === yesterday.toDateString()) {
        return this.translate.instant('chat.yesterday') || 'Yesterday';
      }

      return date.toLocaleDateString('en-US', {
        day: 'numeric',
        month: 'long',
        year: date.getFullYear() !== today.getFullYear() ? 'numeric' : undefined
      });
    } catch {
      return this.translate.instant('chat.invalid_date') || 'Invalid date';
    }
  }

  async sendMessage(): Promise<void> {
    if ((!this.messageText.trim() && this.selectedFiles.length === 0) || !this.currentTicketId || !this.currentUserTicket || this.sendingMessage) return;

    this.sendingMessage = true;

    try {
      let attachmentIds: string[] = [];
      if (this.selectedFiles.length > 0) {
        attachmentIds = await this.uploadFiles(this.selectedFiles);
      }

      const createMessageCommand: CreateMessageCommand = {
        userTicketMessages: {
          userTicketId: this.currentTicketId,
          message: this.messageText.trim() || (this.selectedFiles.length > 0 ? 'Fichier(s) joint(s)' : ''),
          created: new Date().toISOString(),
          attachmentIds: attachmentIds,
          attachmentIdsJson: JSON.stringify(attachmentIds)
        },
        sendEmail: true,
        toEmail: this.currentUserTicket.createUserEmail || ''
      };

      console.log('Sending message with payload:', createMessageCommand);

      this.userTicketService.commonUserTicketCreateMessagePost(createMessageCommand)
        .pipe(
          catchError(error => {
            this.sendingMessage = false;
            console.error('Error sending message:', error);
            return of(null);
          })
        )
        .subscribe(response => {
          this.sendingMessage = false;
          console.log('Message sent successfully:', response);

          if (response) {
            const chatMessage: ChatMessage = {
              id: Date.now().toString(),
              text: this.messageText.trim() || (this.selectedFiles.length > 0 ? `${this.selectedFiles.length} fichier(s) envoyé(s)` : ''),
              time: new Date().toLocaleTimeString('fr-FR', {
                hour12: false,
                hour: '2-digit',
                minute: '2-digit'
              }),
              isOwn: true,
              isRead: false,
              senderName: this.currentUserName
            };

            const attachments = this.createAttachmentsFromIds(attachmentIds, this.selectedFiles);
            if (attachments.length) {
              chatMessage.attachments = attachments;
            }

            this.messages.push(chatMessage);
            this.updateChatListItem(chatMessage);
            this.messageText = '';
            this.selectedFiles = []; 

            setTimeout(() => {
              this.scrollToBottom();
            }, 100);
          }
        });
    } catch (error) {
      this.sendingMessage = false;
      console.error('Error in sendMessage:', error);
    }
  }

  toggleEmojiPicker(): void {
    this.showEmojiPicker = !this.showEmojiPicker;
  }

  toggleVoiceRecording(): void {
    this.isRecordingVoice = !this.isRecordingVoice;
    if (this.isRecordingVoice) {
      this.startVoiceRecording();
    } else {
      this.stopVoiceRecording();
    }
  }

  private startVoiceRecording(): void {
  }

  private stopVoiceRecording(): void {
  }

  onInputFocus(): void {
    this.showEmojiPicker = false;
  }

  onInputBlur(): void {
  }

  scrollToBottom(): void {
    if (this.messagesContainer) {
      const element = this.messagesContainer.nativeElement as HTMLElement;
      element.scrollTop = element.scrollHeight;
      this.showScrollDown = false;
    }
  }

  private buildAuthHeaders(): HttpHeaders {
    const token = localStorage.getItem('access_token') || localStorage.getItem('token');
    let headers = new HttpHeaders();

    if (token) {
      headers = headers.set('Authorization', `Bearer ${token}`);
    }

    return headers;
  }

  private updateChatListItem(message: ChatMessage): void {
    if (this.activeChat) {
      this.updateChatPreview(this.activeChat.id, message);
    }
  }

  onAttachmentClick(): void {
    this.fileInput.nativeElement.click();
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.selectedFiles = Array.from(input.files);
      this.showFileSendModal = true;
    }
  }

  onFileSend(filesWithCaptions: FileToSend[]): void {
    this.sendFilesWithCaptions(filesWithCaptions);
  }

  onFileSendModalClose(): void {
    this.showFileSendModal = false;
    this.selectedFiles = [];
  }

  private async sendFilesWithCaptions(filesWithCaptions: FileToSend[]): Promise<void> {
    if (!this.currentTicketId || !this.currentUserTicket || this.sendingMessage) return;

    this.sendingMessage = true;

    try {
      const uploadedFileIds = await this.uploadFiles(filesWithCaptions.map(fileItem => fileItem.file));

      const messageText = filesWithCaptions[0]?.caption ||
                         (filesWithCaptions.length > 1 ?
                          `${filesWithCaptions.length} fichiers envoyés` :
                          'Fichier envoyé');

      const createMessageCommand: CreateMessageCommand = {
        userTicketMessages: {
          userTicketId: this.currentTicketId,
          emailId: this.currentUserTicket.createUser || '',
          message: messageText,
          created: new Date().toISOString(),
          attachmentIds: uploadedFileIds,
          attachmentIdsJson: JSON.stringify(uploadedFileIds)
        },
        sendEmail: true,
        toEmail: this.currentUserTicket.createUserEmail || ''
      };

      console.log('Sending message with files and captions:', createMessageCommand);

      this.userTicketService.commonUserTicketCreateMessagePost(createMessageCommand)
        .pipe(
          catchError(error => {
            this.sendingMessage = false;
            console.error('Error sending message with files:', error);
            return of(null);
          })
        )
        .subscribe(response => {
          this.sendingMessage = false;
          console.log('Message with files sent successfully:', response);

          if (response) {
            const chatMessage: ChatMessage = {
              id: Date.now().toString(),
              text: messageText,
              time: new Date().toLocaleTimeString('fr-FR', {
                hour12: false,
                hour: '2-digit',
                minute: '2-digit'
              }),
              isOwn: true,
              isRead: false,
              senderName: this.currentUserName
            };

            const attachments = this.createAttachmentsFromIds(uploadedFileIds, filesWithCaptions.map(item => item.file));
            if (attachments.length) {
              chatMessage.attachments = attachments;
            }

            this.messages.push(chatMessage);
            this.updateChatListItem(chatMessage);
            this.selectedFiles = [];
            this.showFileSendModal = false;

            setTimeout(() => {
              this.scrollToBottom();
            }, 0);
          }
        });
    } catch (error) {
      this.sendingMessage = false;
      console.error('Error in sendFilesWithCaptions:', error);
    }
  }

  private async uploadFiles(files: File[]): Promise<string[]> {
    if (!this.currentTicketId || files.length === 0) {
      return [];
    }

    this.uploadingFiles = true;
    const uploadedFileIds: string[] = [];

    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append('UserTicketId', this.currentTicketId);
        formData.append('File', file, file.name);

        const response = await firstValueFrom(
          this.http.post<UploadAttachmentResponse>(
            `${environment.api}/common/UserTicket/UploadAttachment`,
            formData,
            { headers: this.buildAuthHeaders() }
          )
        );

        const fileId = response?.fileId ?? response?.FileId ?? response?.id ?? response?.Id;
        if (fileId) {
          uploadedFileIds.push(fileId);
        } else {
          console.warn('Upload response missing file identifier', response);
        }
      }

      return uploadedFileIds;
    } catch (error) {
      console.error('Error uploading files:', error);
      throw error;
    } finally {
      this.uploadingFiles = false;
    }
  }

  removeSelectedFile(index: number): void {
    this.selectedFiles.splice(index, 1);
  }

  getFileSize(size: number): string {
    if (size < 1024) return size + ' B';
    if (size < 1024 * 1024) return Math.round(size / 1024) + ' KB';
    return Math.round(size / (1024 * 1024)) + ' MB';
  }

  getFileIcon(fileName: string): string {
    const extension = fileName.split('.').pop()?.toLowerCase();
    switch (extension) {
      case 'pdf': return 'pi-file-pdf';
      case 'doc':
      case 'docx': return 'pi-file-word';
      case 'xls':
      case 'xlsx': return 'pi-file-excel';
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif': return 'pi-image';
      case 'zip':
      case 'rar': return 'pi-file-zip';
      default: return 'pi-file';
    }
  }

  // Message search functionality
  filterMessages(): void {
    if (!this.messageSearchQuery.trim()) {
      this.messages = [...this.allMessages];
      return;
    }

    const searchTerm = this.messageSearchQuery.toLowerCase().trim();
    this.messages = this.allMessages.filter(msg => 
      msg.text.toLowerCase().includes(searchTerm) ||
      (msg.senderName && msg.senderName.toLowerCase().includes(searchTerm))
    );
  }

  clearMessageSearch(): void {
    this.messageSearchQuery = '';
    this.filterMessages();
  }

  getStatusLabel(status: number): string {
    const labels: Record<number, string> = {
      0: this.translate.instant('New'),
      1: this.translate.instant('In Progress'),
      2: this.translate.instant('Rejected'),
      3: this.translate.instant('Complete')
    };

    return labels[status] ?? this.translate.instant('Unknown');
  }

  // Check if status change buttons should be shown
  // On admin page, always show buttons when status is InProgress (1) - admin can always change status
  showStatusButtons(): boolean {
    if (!this.currentUserTicket) return false;
    // Show buttons only when status is InProgress (1)
    return this.currentUserTicket.ticketStatus === 1;
  }

  // Update ticket status
  updateTicketStatus(newStatus: number): void {
    if (!this.currentUserTicket || !this.currentTicketId) {
      return;
    }

    this.loading = true;
    const updatedTicket: UserTicket = {
      ...this.currentUserTicket,
      ticketStatus: newStatus as any
    };

    this.userTicketService.commonUserTicketUpdatePut(updatedTicket)
      .pipe(
        catchError((error) => {
          this.loading = false;
          console.error('Error updating ticket status:', error);
          alert(this.translate.instant('Failed to update ticket status'));
          return of(null);
        })
      )
      .subscribe((updated) => {
        this.loading = false;
        if (updated && this.currentTicketId) {
          this.currentUserTicket = updated;
          this.loadAppealInfo(this.currentTicketId);
          // Reload messages to refresh UI
          this.loadMessagesForChat(this.currentTicketId);
          // Update ticket in list
          const index = this.allUserTickets.findIndex(t => t.id === updated.id);
          if (index !== -1) {
            this.allUserTickets[index] = updated;
            this.transformAllUserTicketsToChatList();
          }
        }
      });
  }

  // Close ticket (status 3 - Complete)
  closeTicket(): void {
    this.pendingStatusAction = 3;
    this.showCloseTicketModal = true;
  }

  // Reject ticket (status 2 - Rejected)
  rejectTicket(): void {
    this.pendingStatusAction = 2;
    this.showRejectTicketModal = true;
  }

  // Handle confirmation for status change
  onConfirmStatusChange(): void {
    if (this.pendingStatusAction !== null) {
      this.updateTicketStatus(this.pendingStatusAction);
      this.pendingStatusAction = null;
    }
  }

  // Handle cancel for status change
  onCancelStatusChange(): void {
    this.pendingStatusAction = null;
  }

  getStatusSeverity(status: number): string {
    const severity: Record<number, string> = {
      0: 'info',
      1: 'warning',
      2: 'danger',
      3: 'success'
    };

    return severity[status] ?? 'secondary';
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
